// Stores
import { useUserDataStore } from '@/stores/userDataStore';

// Types
import {
  AllEventsDetailsResponse,
  AllMealsDetailsResponse,
  AllRegisteredEntrantsResponse,
  AllShiftDaysResponse,
  AttendeeListResponse,
  CalendarEventsResponse,
  CarRegistrationDataResponse,
  ClearEntrantsResponse,
  CoEntrantOrGuestPCAMemberDetailsResponse,
  CompletionProgressResponse,
  DeleteCarRegistrationResponse,
  DeleteEventRegistrationResponse,
  DeleteMealRegistrationResponse,
  DeleteUnpaidEntrantResponse,
  EntrantTypesResponse,
  EventClassesResponse,
  EventConflictsResponse,
  EventContactsResponse,
  EventDetailsResponse,
  EventRegistrationDataResponse,
  EventSubClassesResponse,
  GendersResponse,
  ItineraryDataResponse,
  LoginUserResponse,
  MealPackagesResponse,
  MealRegistrationDataResponse,
  MemberCarsResponse,
  MemberDetailsResponse,
  NationalRegionalTitlesResponse,
  OrderHistoryResponse,
  PCAAgeRangesResponse,
  PCAJuniorDetailsResponse,
  PaymentSummaryResponse,
  PrimaryEntrantDetailsResponse,
  RegionsResponse,
  ResourcesResponse,
  SaveAutoXEventResponse,
  SaveCarResponse,
  SaveChildResponse,
  SaveCoEntrantResponse,
  SaveCompletionProgressResponse,
  SaveConcoursEventResponse,
  SaveGuestResponse,
  SaveJuniorsAtParadeEventResponse,
  SaveMealsResponse,
  SavePCAJuniorResponse,
  SavePrimaryEntrantResponse,
  SaveSimRacingEventResponse,
  SaveStandardEventResponse,
  SaveToWaitlistResponse,
  SearchNavigatorsResponse,
  SendItineraryEmailResponse,
  ShirtSizesResponse,
  SimRacingTimeSlotsResponse,
  SubmitContactUsMessageResponse,
  SubmitPaymentResponse,
  UserCoMemberDetailsResponse,
  VehicleBodyStyleResponse,
  VehicleColorResponse,
  VehicleTaxonomyResponse,
} from '@/types/apiResponses';
import {
  FetchDeleteUnpaidEntrantRequestBody,
  FetchLoginUserRequestBody,
  FetchSaveAutoXEventRequestBody,
  FetchSaveCarRequestBody,
  FetchSaveChildRequestBody,
  FetchSaveCoEntrantRequestBody,
  FetchSaveConcoursEventRequestBody,
  FetchSaveGuestRequestBody,
  FetchSaveJuniorsAtParadeEventRequestBody,
  FetchSaveMealsRequestBody,
  FetchSavePCAJuniorRequestBody,
  FetchSavePrimaryEntrantRequestBody,
  FetchSaveSimRacingEventRequestBody,
  FetchSaveStandardEventRequestBody,
  FetchSubmitContactUsMessageRequestBody,
  FetchSubmitPaymentRequestBody,
} from '@/types/apiRequests';
import { TimeSlotsResponse } from 'parade_types';

// Utils
import axios from 'axios';

// The CF backend returns HTTP 200 with `{ DATA: '0', MSG: 'User does not have access' }`
// when access is denied (e.g. registration window is closed). Convert that into a
// recognizable error so the app shell can show a friendly message instead of the
// per-fetcher "Invalid X response" errors that lose the original reason.
export class AccessDeniedError extends Error {
  constructor(message = 'User does not have access') {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

axios.interceptors.response.use((response) => {
  const data = response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const msg = (data.MSG ?? data.msg ?? '').toString();
    if (msg.toLowerCase().includes('does not have access')) {
      return Promise.reject(new AccessDeniedError(msg));
    }
  }
  return response;
});

// In dev, use the /parade-proxy prefix so the webpack dev server proxies to whatever
// VUE_APP_API_URL is set to in .env.development (or .env.local). This avoids CORS issues
// regardless of whether the target is local ColdFusion or a remote server.
// In production, use VUE_APP_API_URL directly.
const handlerURL: string = process.env.NODE_ENV === 'production'
  ? (process.env.VUE_APP_API_URL as string)
  : '/parade-proxy/index.cfm?event=';

const ageRanges: string = 'main.getAgeRanges';
const clearEntrants: string = 'member.clearEntrant';
const userCoMemberDetails: string = 'member.getUserCoMember';
const coEntrantOrGuestPCAMemberDetails: string = 'member.getCoEntrantDetails';
const completionProgress: string = 'member.getProgress';
const orderHistory: string = 'member.getOrderHistory';
const deleteUnpaidEntrant: string = 'member.removeUnpaidEntrant';
const entrantTypes: string = 'main.getEntrantTypes';
const eventSettings: string = 'main.getEventSettings';
const nationalRegionalTitles: string = 'member.getTitles';
const attendeeList: string = 'member.attendeeList';
const genders: string = 'main.getGenders';
const juniorDetails: string = 'member.getJuniorsDetails';
const loginUser: string = 'member.login';
const memberCars: string = 'member.getMemberCars';
const memberDetails: string = 'member.getMemberDetails';
const entrantCalendarDetails: string = 'member.entrantCalendarDetails';
const paymentSummary: string = 'member.paymentSummary';
const primaryEntrantDetails: string = 'member.getEntrantDetails';
const regions: string = 'main.getRegions';
const saveCar: string = 'member.savecar';
const saveChild: string = 'member.saveChild';
const saveCoEntrant: string = 'member.coEntrantSave';
const saveCompletionProgress: string = 'member.updateProgress';
const saveGuest: string = 'member.guestSave';
const savePCAJunior: string = 'member.juniorSave';
const savePrimaryEntrant: string = 'member.primaryEntrantSave';
const submitContactUsMessage: string = 'member.submitContactUsMessage';
const submitPayment: string = 'member.entrantPayment';
const allRegisteredEntrants: string = 'member.getentireparty';
const vehicleTaxonomy: string = 'member.getvehicles';
const vehicleBodyStyle: string = 'member.getBodystyles';
const vehicleColors: string = 'main.getColors';
const eventDetails: string = 'main.getParadeEventDetails';
const shirtSizes: string = 'main.shirtsizes';
const eventClasses: string = 'main.getLookupBundles';
const eventSubClasses: string = 'main.getEventLookups';
const eventContacts: string = 'main.getContacts';
const shiftDays: string = 'main.shiftDays';
const saveStandardEvent: string = 'member.saveEventReg';
const mealDetails: string = 'main.getParadeMeals';
const saveMeals: string = 'member.saveMealsReg';
const mealPackages: string = 'member.getMealPackages';
const saveJuniorsAtParadeEvent: string = 'member.saveJuniorsAtParade';
const saveAutoXParadeEvent: string = 'member.saveAutoX';
const getTimeSlots: string = 'main.timeSlots';
const getSimRacingTimeSlots: string = 'main.simracingtimeSlots';
const saveSimRacingParadeEvent: string = 'member.saveSimRacing';
const searchNavigators: string = 'member.findNavigator';
const getEventRegData: string = 'member.getEntrantEvent';
const getMealRegData: string = 'member.getEntrantMeal';
const deleteEventReg: string = 'member.removeUnpaidEvent';
const deleteMealReg: string = 'member.removeUnpaidMeal';
const deleteCarReg: string = 'member.deleteCar';
const getCarRegData: string = 'member.getCarEdit';
const saveConcoursParadeEvent: string = 'member.saveConcours';
const getVolEventsByDate: string = 'main.volEventsByDate';
const getVolJobsByCategoryID: string = 'main.volJobsByCategoryID';
const getVolShiftsByJobID: string = 'main.volShiftsByJobID';
const saveVolunteer: string = 'member.saveVolunteer';
const getEntrantVolDetails: string = 'member.getEntrantVolDetails';
const saveToWaitlist: string = 'member.saveWaitlist';
const getItinerary: string = 'member.itinerary';
const sendItineraryEmail: string = 'member.itineraryemail';
const getEventConflicts: string = 'member.findOverLappingEvents';
const getResources: string = 'main.getResources';

export const fetchEventConflicts = async (eventId: number): Promise<EventConflictsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEventConflicts: authToken is null');
  } else {
    const response = await axios.post(`${handlerURL}${getEventConflicts}`, {
      thekey: authToken,
      event_id: eventId,
    });
    if ('data' in response) {
      return response.data;
    } else {
      throw new Error(`Invalid response from ${getEventConflicts} endpoint`);
    }
  }
};

export const fetchResources = async (): Promise<ResourcesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchResources: authToken is null');
  } else {
    const response = await axios.post(`${handlerURL}${getResources}`, {
      thekey: authToken,
    });
    if ('data' in response) {
      return response.data;
    } else {
      throw new Error(`Invalid response from ${getResources} endpoint`);
    }
  }
};

export const fetchSendItineraryEmail = async (): Promise<SendItineraryEmailResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSendItineraryEmail: authToken is null');
  } else {
    const response = await axios.post(`${handlerURL}${sendItineraryEmail}`, {
      thekey: authToken,
    });
    if ('data' in response) {
      return response.data;
    } else {
      throw new Error(`Invalid response from ${sendItineraryEmail} endpoint`);
    }
  }
};

export const fetchItineraryData = async (): Promise<ItineraryDataResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchGetItinerary: authToken is null');
  } else {
    const response = await axios.post(`${handlerURL}${getItinerary}`, {
      thekey: authToken,
    });
    if ('data' in response) {
      return response.data;
    } else {
      throw new Error(`Invalid response from ${getItinerary} endpoint`);
    }
  }
};

export const fetchSaveToWaitlist = async (eventId: number): Promise<SaveToWaitlistResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('saveToWaitlist: authToken is null');
  } else {
    const response = await axios.post(`${handlerURL}${saveToWaitlist}`, {
      thekey: authToken,
      event_id: eventId,
    });
    if ('data' in response) {
      return response.data;
    } else {
      throw new Error(`Invalid response from ${saveToWaitlist} endpoint`);
    }
  }
}; 

export const fetchEntrantVolDetails = async (entrantid: number): Promise<any> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveVolunteer: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getEntrantVolDetails}`, {
    thekey: authToken,
    entrantid: entrantid
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid Response from ${getEntrantVolDetails} endpoint`);
  }
};

export const fetchSaveVolunteer = async (volunteerData: any[]): Promise<any> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveVolunteer: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveVolunteer}`, {
    thekey: authToken,
    volunteerdata: volunteerData
  });
  if ('data' in response) {
    await userDataStore.requestPaymentSummary();
    return response.data;
  } else {
    throw new Error(`Invalid Response from ${saveVolunteer} endpoint`);
  }
};

export const fetchVolShiftsByJobID = async (jobId: string, shiftDay: string): Promise<any> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchVolEventsByDate: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getVolShiftsByJobID}`, {
    thekey: authToken,
    volunteer_job_id: jobId,
    timevalue: shiftDay
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid Response from ${getVolShiftsByJobID} endpoint`);
  }
};

export const fetchVolJobsByCategoryID = async (jobId: string, shiftDay: string): Promise<any> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchVolEventsByDate: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getVolJobsByCategoryID}`, {
    thekey: authToken,
    volunteer_job_id: jobId,
    timevalue: shiftDay
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid Response from ${getVolJobsByCategoryID} endpoint`);
  }
};

export const fetchVolEventsByDate = async (date: string): Promise<any> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchVolEventsByDate: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getVolEventsByDate}`, {
    thekey: authToken,
    timevalue: date
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid Response from ${getVolEventsByDate} endpoint`);
  }
};

export const fetchDeleteCarRegistration = async (carId: number): Promise<DeleteCarRegistrationResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchDeleteCarRegistration: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${deleteCarReg}`, {
    thekey: authToken,
    CAR_ID: carId,
  });
  if (response.data.data != 1) {
    throw new Error(`Error deleting car registration: ${response.data.msg}`);
  }
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchDeleteMealRegistration = async (mealId: number, entrantId: number): Promise<DeleteMealRegistrationResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchDeleteMealRegistration: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${deleteMealReg}`, {
    thekey: authToken,
    PARADE_MEAL_ID: mealId,
    ENTRANTID: entrantId,
  });
  if (response.data.data != 1) {
    throw new Error(`Error deleting meal registration: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchDeleteEventRegistration = async (eventId: number, entrantId: number, carId: number): Promise<DeleteEventRegistrationResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchDeleteEventRegistration: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${deleteEventReg}`, {
    thekey: authToken,
    eventid: eventId,
    entrantid: entrantId,
    CAR_ID: carId,
  });
  if (response.data.data != 1) {
    throw new Error(`Error deleting event registration: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchMealRegistrationData = async (mealId: number): Promise<MealRegistrationDataResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchMealRegistrationData: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getMealRegData}`, {
    thekey: authToken,
    mealId: mealId,
  });
  if (Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid entrants meal registration data response from ${getMealRegData} endpoint`);
  }
};

export const fetchEventRegistrationData = async (eventId: number): Promise<EventRegistrationDataResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEventRegistrationData: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getEventRegData}`, {
    thekey: authToken,
    eventid: eventId,
  });
  if (Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid entrants event registration data response from ${getEventRegData} endpoint`);
  }
};

export const fetchCarRegData = async (carId: number): Promise<CarRegistrationDataResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchCarRegData: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getCarRegData}`, {
    thekey: authToken,
    car_id: carId,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid car registration data response from ${getCarRegData} endpoint`);
  }
};

export const fetchSearchNavigators = async (search: string): Promise<SearchNavigatorsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSearchNavigators: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${searchNavigators}`, {
    thekey: authToken,
    search,
  });
  if (Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid search navigators response from ${searchNavigators} endpoint`);
  }
};

export const fetchSaveConcoursEvent = async (payload: FetchSaveConcoursEventRequestBody): Promise<SaveConcoursEventResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveConcoursEvent: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveConcoursParadeEvent}`, {
    thekey: authToken,
    entrantdata: payload,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving concours event: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchSaveSimRacingEvent = async (payload: FetchSaveSimRacingEventRequestBody): Promise<SaveSimRacingEventResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveSimRacingEvent: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveSimRacingParadeEvent}`, {
    thekey: authToken,
    entrants: payload,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving sim racing event: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchTimeSlots = async (): Promise<TimeSlotsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchGetTimeSlots: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getTimeSlots}`, {
    thekey: authToken,
  });
  if ('data' in response && Array.isArray(response.data)) {
    return response.data as TimeSlotsResponse;
  } else {
    throw new Error('Error getting time slots');
  }
};

export const fetchSimRacingTimeSlots = async (eventId: 199 | 235): Promise<SimRacingTimeSlotsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSimRacingTimeSlots: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${getSimRacingTimeSlots}`, {
    thekey: authToken,
    event_id: eventId,
  });
  if ('data' in response && Array.isArray(response.data)) {
    return {
      day1: response.data[0]['day1'],
      day2: response.data[1]['day2'],
      day3: response.data[2]['day3'],
      day4: response.data[3]['day4'],
      day5: response.data[4]['day5'],
    };
  } else {
    throw new Error('fetchSimRacingTimeSlots: invalid response');
  }
};

export const fetchSaveAutoXEvent = async (payload: FetchSaveAutoXEventRequestBody): Promise<SaveAutoXEventResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveAutoXParadeEvent: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveAutoXParadeEvent}`, {
    thekey: authToken,
    entrants: payload,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving autoX event: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchSaveJuniorsAtParadeEvent = async (payload: FetchSaveJuniorsAtParadeEventRequestBody): Promise<SaveJuniorsAtParadeEventResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveJuniorsAtParadeEvent: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveJuniorsAtParadeEvent}`, {
    thekey: authToken,
    entrantData: payload,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving juniors event: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchMealPackages = async (): Promise<MealPackagesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchMealPackages: authToken is null');
  }
  // const response = await axios.post(`${handlerURL}${mealPackages}`, {
  //   thekey: authToken,
  // });
  const response = { // @ZACK - remove after testing
    data: [
      {
        NAME: 'Meal Package 1',
        ID: 1,
        MEAL_IDS: [2, 3, 5]
      },
      {
        NAME: 'Meal Package 2',
        ID: 2,  
        MEAL_IDS: [4, 6, 7] 
      }
    ]
  }
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid meal packages response from ${mealPackages} endpoint`);
  }
};

export const fetchSaveMeals = async (payload: FetchSaveMealsRequestBody): Promise<SaveMealsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveMeals: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveMeals}`, {
    thekey: authToken,
    entrantdata: payload,
  });
  if (response.data.data != 1) {
    throw new Error(response.data.msg);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchSaveStandardEvent = async (payload: FetchSaveStandardEventRequestBody): Promise<SaveStandardEventResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveStandardEvent: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveStandardEvent}`, {
    thekey: authToken,
    entrantdata: payload,
  });
  if (response.data.data != 1) {
    throw new Error(response.data.msg);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchAllShiftDays = async (): Promise<AllShiftDaysResponse> => {
  const userDataStore= useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  const response = await axios.post(`${handlerURL}${shiftDays}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid all events details response from ${shiftDays} endpoint`);
  }
};

export const fetchAllMealsDetails = async (): Promise<AllMealsDetailsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchAllMealsDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${mealDetails}`, {
    thekey: authToken,
    parade_meal_id: 0,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid all meals details response from ${mealDetails} endpoint`);
  }
};

export const fetchAllEventsDetails = async (): Promise<AllEventsDetailsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchAllEventsDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${eventDetails}`, {
    thekey: authToken,
    event_id: 0,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid all events details response from ${eventDetails} endpoint`);
  }
};

export const fetchEventContacts = async (): Promise<EventContactsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEventContacts: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${eventContacts}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid event contacts response from ${eventContacts} endpoint`);
  }
};

export const fetchShirtSizes = async (): Promise<ShirtSizesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchShirtSizes: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${shirtSizes}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid shirtSizes response from ${shirtSizes} endpoint`);
  }
};

export const fetchEventClasses = async (): Promise<EventClassesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEventClasses: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${eventClasses}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid eventClasses response from ${eventClasses} endpoint`);
  }
};

export const fetchEventSubClasses = async (
  classShortCode: string
): Promise<EventSubClassesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEventSubClasses: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${eventSubClasses}`, {
    thekey: authToken,
    shortcode: classShortCode,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid eventSubClasses response from ${eventSubClasses} endpoint`);
  }
};

export const fetchAllRegisteredEntrants = async (): Promise<AllRegisteredEntrantsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchAllRegisteredEntrants: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${allRegisteredEntrants}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(
      `Invalid all registered entrants response from ${allRegisteredEntrants} endpoint`
    );
  }
};

export const fetchLoginUser = async (
  requestBody: FetchLoginUserRequestBody
): Promise<LoginUserResponse> => {
  const response = await axios.post(`${handlerURL}${loginUser}`, requestBody);
  if ('data' in response && 'status' in response.data && 'msg' in response.data) {
    return response.data;
  } else {
    throw new Error(`Invalid login user response from ${loginUser} endpoint`);
  }
};

export const fetchDeleteUnpaidEntrant = async (
  requestBody: FetchDeleteUnpaidEntrantRequestBody
): Promise<DeleteUnpaidEntrantResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchDeleteUnpaidEntrant: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${deleteUnpaidEntrant}`, {
    thekey: authToken,
    ...requestBody,
  });
  if ('data' in response) {
    await userDataStore.requestPaymentSummary();
    return response.data;
  } else {
    throw new Error(`Invalid delete unpaid entrant response from ${deleteUnpaidEntrant} endpoint`);
  }
};

export const fetchSubmitContactUsMessage = async (
  requestBody: FetchSubmitContactUsMessageRequestBody
): Promise<SubmitContactUsMessageResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSubmitContactUsMessage: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${submitContactUsMessage}`, {
    thekey: authToken,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error submitting contact us message: ${response.data.msg}`);
  } 
  return response.data;
};

export const fetchSubmitPayment = async (
  memberNumber: number,
  requestBody: FetchSubmitPaymentRequestBody
): Promise<SubmitPaymentResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSubmitPayment: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${submitPayment}`, {
    thekey: authToken,
    membernumber: memberNumber,
    ...requestBody,
  });
  if (response.data.data != '1' && response.data.data != '404') {
    console.log('here e')
    throw new Error(`Error submitting payment: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data; 
};

export const fetchSaveCompletionProgress = async (
  formTypeToMarkComplete:
    | 'PRIMARYENTRANT'
    | 'COENTRANT'
    | 'PCA_JUNIOR_OR_CHILD'
    | 'GUEST'
    | 'CHECKOUT'
): Promise<SaveCompletionProgressResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveCompletionProgress: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveCompletionProgress}`, {
    thekey: authToken,
    progressval: formTypeToMarkComplete,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving autoX event: ${response.data.msg}`);
  } 
  return response.data;
};

// This endpoint returns `null` if there is 0% completion - otherwise it returns the CompletionProgressResponse object
export const fetchCompletionProgress = async (): Promise<CompletionProgressResponse | null> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchCompletionProgress: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${completionProgress}`, {
    thekey: authToken,
  });
  if (Array.isArray(response.data) && response.data[0] !== undefined) {
    return response.data[0];
  } else if (Array.isArray(response.data) && response.data.length === 0) {
    return null;
  } else {
    throw new Error(`Invalid completion progress response from ${completionProgress} endpoint`);
  }
};

export const fetchSavePrimaryEntrant = async (
  memberNumber: number,
  requestBody: FetchSavePrimaryEntrantRequestBody
): Promise<SavePrimaryEntrantResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSavePrimaryEntrant: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${savePrimaryEntrant}`, {
    thekey: authToken,
    membernumber: memberNumber,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving primary entrant: ${response.data.msg}`);
  } 
  return response.data;
};

export const fetchSaveCoEntrant = async (
  requestBody: FetchSaveCoEntrantRequestBody
): Promise<SaveCoEntrantResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveCoEntrant: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveCoEntrant}`, {
    thekey: authToken,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving co-entrant: ${response.data.msg}`);
  } 
  return response.data;
};

export const fetchSaveGuest = async (
  requestBody: FetchSaveGuestRequestBody
): Promise<SaveGuestResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveGuest: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveGuest}`, {
    thekey: authToken,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving guest: ${response.data.msg}`);
  } 
  return response.data;
};

export const fetchSavePCAJunior = async (
  memberNumber: number,
  requestBody: FetchSavePCAJuniorRequestBody
): Promise<SavePCAJuniorResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSavePCAJunior: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${savePCAJunior}`, {
    thekey: authToken,
    membernumber: memberNumber,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving PCA junior: ${response.data.msg}`);
  } 
  return response.data;
};

export const fetchSaveChild = async (
  requestBody: FetchSaveChildRequestBody
): Promise<SaveChildResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveChild: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveChild}`, {
    thekey: authToken,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving child: ${response.data.msg}`);
  } 
  return response.data;
};

export const fetchSaveCar = async (
  requestBody: FetchSaveCarRequestBody
): Promise<SaveCarResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchSaveCar: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${saveCar}`, {
    thekey: authToken,
    membernumber: userDataStore.getMemberNumber,
    ...requestBody,
  });
  if (response.data.data != 1) {
    throw new Error(`Error saving car: ${response.data.msg}`);
  } 
  await userDataStore.requestPaymentSummary();
  return response.data;
};

export const fetchClearEntrants = async (): Promise<ClearEntrantsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchClearEntrants: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${clearEntrants}`, {
    thekey: authToken,
  });
  if ('data' in response && 'data' in response.data && response.data.data === 1) {
    return response.data;
  } else {
    throw new Error(`Invalid clear entrants response from ${clearEntrants} endpoint`);
  }
};

export const fetchMemberCars = async (): Promise<MemberCarsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchMemberCars: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${memberCars}`, {
    thekey: authToken,
    membernumber: userDataStore.getMemberNumber,
  });
  if (Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid member cars response from ${memberCars} endpoint`);
  }
};

export const fetchMemberDetails = async (): Promise<MemberDetailsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchMemberDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${memberDetails}`, {
    thekey: authToken,
  });
  if (Array.isArray(response.data) && response.data[0] !== undefined) {
    return response.data[0];
  } else {
    throw new Error(`Invalid member details response from ${memberDetails} endpoint`);
  }
};

export const fetchVehicleTaxonomy = async (year: number): Promise<VehicleTaxonomyResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchVehicleTaxonomy: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${vehicleTaxonomy}`, {
    thekey: authToken,
    year,
  });
  if ('data' in response && Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid vehicle taxonomy response from ${vehicleTaxonomy} endpoint`);
  }
};

export const fetchVehicleBodyStyle = async (): Promise<VehicleBodyStyleResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchVehicleBodyStyle: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${vehicleBodyStyle}`, {
    thekey: authToken,
  });
  if ('data' in response && Array.isArray(response.data) && response.data.length > 0) {
    return response.data as VehicleBodyStyleResponse;
  } else {
    throw new Error(`Invalid vehicle body style response from ${vehicleBodyStyle} endpoint`);
  }
};

export const fetchVehicleColors = async (): Promise<VehicleColorResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchVehicleColors: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${vehicleColors}`, {
    thekey: authToken,
  });
  if ('data' in response && Array.isArray(response.data) && response.data.length > 0) {
    return response.data as VehicleColorResponse;
  } else {
    throw new Error(`Invalid vehicle colors response from ${vehicleColors} endpoint`);
  }
};

export const fetchUserCoMemberDetails = async (): Promise<UserCoMemberDetailsResponse | null> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchUserCoMemberDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${userCoMemberDetails}`, {
    thekey: authToken,
  });
  if ('data' in response && Array.isArray(response.data) && response.data[0] !== undefined) {
    return response.data[0];
  } else if ('data' in response && Object.keys(response.data).length === 0) {
    return null;
  } else {
    throw new Error(`Invalid user co-member details response from ${userCoMemberDetails} endpoint`);
  }
};

export const fetchOrderHistory = async (): Promise<OrderHistoryResponse | null> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchOrderHistory: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${orderHistory}`, {
    thekey: authToken,
  });
  if ('data' in response && Array.isArray(response.data) && response.data.length > 0) {
    return response.data;
  } else if ('data' in response && Array.isArray(response.data) && response.data.length === 0) {
    return null;
  } else {
    throw new Error(`Invalid order history response from ${orderHistory} endpoint`);
  }
};

export const fetchPrimaryEntrantDetails =
  async (): Promise<PrimaryEntrantDetailsResponse | null> => {
    const userDataStore = useUserDataStore();
    const authToken: string | null = userDataStore.getAuthToken;
    if (authToken === null) {
      throw new Error('fetchPrimaryEntrantDetails: authToken is null');
    }
    const response = await axios.post(`${handlerURL}${primaryEntrantDetails}`, {
      thekey: authToken,
    });
    if (Array.isArray(response.data) && response.data[0] !== undefined) {
      return response.data[0];
    } else if (Array.isArray(response.data) && response.data.length === 0) {
      return null;
    } else {
      throw new Error(
        `Invalid primary entrant details response from ${primaryEntrantDetails} endpoint`
      );
    }
  };

export const fetchPaymentSummary = async (): Promise<PaymentSummaryResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchPaymentSummary: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${paymentSummary}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid payment summary response from ${paymentSummary} endpoint`);
  }
};

export const fetchCoEntrantOrGuestPCAMemberDetails = async (
  coEntrantOrGuestPCAMemberNumber: number
): Promise<CoEntrantOrGuestPCAMemberDetailsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchCoEntrantOrGuestPCAMemberDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${coEntrantOrGuestPCAMemberDetails}`, {
    thekey: authToken,
    membernumber: coEntrantOrGuestPCAMemberNumber,
  });
  if (Array.isArray(response.data) && response.data[0] !== undefined) {
    return response.data[0];
  } else {
    console.error(
      `Invalid co-entrant/guest PCA member details response from ${coEntrantOrGuestPCAMemberDetails} endpoint`
    );
    throw new Error(`Member is already registered or the number is invalid`);
  }
};

export const fetchJuniorDetails = async (
  associatedMemberNumber: number
): Promise<PCAJuniorDetailsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchJuniorDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${juniorDetails}`, {
    thekey: authToken,
    membernumber: associatedMemberNumber,
  });
  if (Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid junior details response from ${juniorDetails} endpoint`);
  }
};

export const fetchEventDetails = async (): Promise<EventDetailsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEventDetails: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${eventSettings}`, { thekey: authToken });
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data[0];
  } else {
    throw new Error(`Invalid event details response from ${eventSettings} endpoint`);
  }
};

export const fetchNationalRegionalTitles = async (
  memberNumber: number
): Promise<NationalRegionalTitlesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchNationalRegionalTitles: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${nationalRegionalTitles}`, {
    thekey: authToken,
    memberNumber,
  });
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data.map((item: { NAME: string }) => item.NAME);
  } else {
    throw new Error(
      `Invalid national regional titles response from ${nationalRegionalTitles} endpoint`
    );
  }
};

export const fetchRegions = async (): Promise<RegionsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchRegions: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${regions}`, { thekey: authToken });
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data.slice(1); // The item at index 0 is null for some reason - get 'er out of there!
  } else {
    throw new Error(`Invalid regions response from ${regions} endpoint`);
  }
};

export const fetchEntrantTypes = async (): Promise<EntrantTypesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchEntrantTypes: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${entrantTypes}`, { thekey: authToken });
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data as EntrantTypesResponse;
  } else {
    throw new Error(`Invalid entrant types response from ${entrantTypes} endpoint`);
  }
};

export const fetchPCAAgeRanges = async (): Promise<PCAAgeRangesResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchPCAAgeRanges: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${ageRanges}`, { thekey: authToken });
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data as PCAAgeRangesResponse;
  } else {
    throw new Error(`Invalid PCA age ranges response from ${ageRanges} endpoint`);
  }
};

export const fetchGenders = async (): Promise<GendersResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchGenders: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${genders}`, { thekey: authToken });
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data as GendersResponse;
  } else {
    throw new Error(`Invalid genders response from ${genders} endpoint`);
  }
};

export const fetchAttendeeList = async (): Promise<AttendeeListResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchAttendeeList: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${attendeeList}`, {
    thekey: authToken,
  });
  if ('data' in response && Array.isArray(response.data)) {
    return response.data;
  } else {
    throw new Error(`Invalid attendee list response from ${attendeeList} endpoint`);
  }
};

export const fetchCalendarEvents = async (): Promise<CalendarEventsResponse> => {
  const userDataStore = useUserDataStore();
  const authToken: string | null = userDataStore.getAuthToken;
  if (authToken === null) {
    throw new Error('fetchCalendarEvents: authToken is null');
  }
  const response = await axios.post(`${handlerURL}${entrantCalendarDetails}`, {
    thekey: authToken,
  });
  if ('data' in response) {
    return response.data;
  } else {
    throw new Error(`Invalid calendar events response from ${entrantCalendarDetails} endpoint`);
  }
};
